import { particleAtlas } from "./particles";
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

type Placement = [number, number, number, number?, number?, number?];
const dummy = new THREE.Object3D();

/** Bake source transforms and material colours so static parts share a draw. */
export function coloredGeometry(root: THREE.Object3D): THREE.BufferGeometry {
  root.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  root.traverseVisible((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const mesh = node as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.Material | THREE.Material[]
    >;
    const source = mesh.geometry;
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    const geometry = source.index ? source.toNonIndexed() : source.clone();
    geometry.applyMatrix4(mesh.matrixWorld);
    const count = geometry.getAttribute("position").count;
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const group = source.groups.find(
        (g) => i >= g.start && i < g.start + g.count,
      );
      const material = (materials[group?.materialIndex ?? 0] ??
        materials[0]) as THREE.MeshStandardMaterial;
      const color =
        material.name === "mat_pine_foliage"
          ? new THREE.Color("white")
          : (material.color ?? new THREE.Color("white"));
      const tint = geometry.getAttribute("color");
      colors.set(
        [
          color.r * (tint?.getX(i) ?? 1),
          color.g * (tint?.getY(i) ?? 1),
          color.b * (tint?.getZ(i) ?? 1),
        ],
        i * 3,
      );
    }
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    for (const name of Object.keys(geometry.attributes))
      if (!["position", "normal", "color"].includes(name))
        geometry.deleteAttribute(name);
    geometry.clearGroups();
    parts.push(geometry);
  });
  const merged = mergeGeometries(parts);
  if (!merged) throw new Error(`Cannot merge ${root.name}`);
  return merged;
}

function softTexture(): THREE.DataTexture {
  return particleAtlas;
}
const vertexMaterial = () =>
  new THREE.MeshBasicMaterial({ vertexColors: true });

export class Backyard extends THREE.Group {
  readonly entities = new Map<string, THREE.Object3D>();
  readonly snow: THREE.InstancedMesh;
  readonly smoke: THREE.InstancedMesh;
  readonly glow: THREE.InstancedMesh;
  constructor() {
    super();
    const map = softTexture();
    this.snow = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map,
        transparent: true,
        opacity: 0.88,
        depthWrite: false,
        fog: false,
      }),
      84,
    );
    this.smoke = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        color: 0xffedf5,
      }),
      7,
    );
    this.glow = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
        color: 0xffc24a,
        blending: THREE.AdditiveBlending,
      }),
      18,
    );
    for (const mesh of [this.snow, this.smoke, this.glow]) {
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    this.add(this.snow, this.smoke, this.glow);
  }
  update(
    time: number,
    camera: THREE.PerspectiveCamera,
    reduceMotion: boolean,
    height: number,
  ): void {
    camera.updateMatrixWorld(true);
    const seconds = time / 1000;
    this.snow.count = reduceMotion ? 30 : 84;
    for (let i = 0; i < this.snow.count; i++) {
      const large = i >= 70;
      const speed =
        (large ? 1.2 : 0.4 + (i % 6) * 0.1) * (reduceMotion ? 0.5 : 1);
      const depth = large ? 2.5 : 8 + (i % 13);
      const halfH = Math.tan((camera.fov * Math.PI) / 360) * depth;
      const y = 1 - ((i * 0.6180339 + (seconds * speed) / (halfH * 2)) % 1) * 2;
      dummy.position
        .set(
          (((i * 0.7548777) % 1) * 2 - 1) * halfH * camera.aspect +
            0.15 * Math.sin(seconds * 0.3),
          y * halfH,
          -depth,
        )
        .applyMatrix4(camera.matrixWorld);
      dummy.quaternion.copy(camera.quaternion);
      dummy.scale.setScalar(
        ((large ? 9 + (i % 5) : 2.4 + (i % 5)) * halfH * 2) / height,
      );
      dummy.updateMatrix();
      this.snow.setMatrixAt(i, dummy.matrix);
    }
    for (let i = 0; i < 7; i++) {
      const p =
        ((seconds * (reduceMotion ? 0.125 : 0.25) + i * 0.115) % 0.8) / 0.8;
      dummy.position.set(
        -1.75 + Math.sin(p * 3 + i) * 0.06,
        3.8 + p * 1.5,
        -12,
      );
      dummy.quaternion.copy(camera.quaternion);
      dummy.scale.setScalar(0.24 + 0.46 * p);
      dummy.updateMatrix();
      this.smoke.setMatrixAt(i, dummy.matrix);
      this.smoke.setColorAt(i, new THREE.Color().setScalar(1 - p));
    }
    for (let i = 0; i < 18; i++) {
      if (i < 2) dummy.position.set(i ? 2.6 : -2.6, 1.25, -1);
      else if (i < 4) dummy.position.set(-3.6 + (i - 2) * 2.15, 1.36, -10.65);
      else {
        const t = (i - 4 + 0.5) / 14;
        dummy.position.set(-3 + 6 * t, 2.29 - 2 * t * (1 - t), -9);
      }
      dummy.quaternion.copy(camera.quaternion);
      dummy.scale.setScalar(i < 4 ? 0.65 : 0.18);
      dummy.updateMatrix();
      this.glow.setMatrixAt(i, dummy.matrix);
      this.glow.setColorAt(
        i,
        new THREE.Color().setScalar(
          reduceMotion
            ? 0.8
            : 0.8 + 0.2 * Math.sin(seconds * (0.4 + (i % 3) * 0.13) + i * 2.7),
        ),
      );
    }
    for (const mesh of [this.snow, this.smoke, this.glow]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
}

export async function loadBackyard(
  load: (path: string) => Promise<THREE.Group>,
): Promise<Backyard> {
  const yard = new Backyard();
  const names = [
    "pine",
    "hill",
    "mound",
    "cloud",
    "candy_stake",
    "props_v2/house",
    "props_v2/fort",
    "props_v2/drift",
    "props_v2/lantern",
    "props_v2/string_lights",
  ];
  const roots = await Promise.all(names.map((name) => load(`${name}.glb`)));
  const shadows: Placement[] = [
    [0, 0, 1.6, 0.72],
    [0, 0, -4.6, 0.62],
    [-2.6, 0, -12, 1.8],
  ];
  const models = new Map(names.map((name, i) => [name, roots[i]!]));
  const place = (name: string, placements: Placement[], colors?: number[]) => {
    const geometry = coloredGeometry(models.get(name)!);
    if (name.endsWith("drift")) {
      const positions = geometry.getAttribute("position"),
        colors = geometry.getAttribute("color");
      for (let i = 0; i < positions.count; i++) {
        const shade = 0.94 + 0.04 * positions.getX(i);
        colors.setXYZ(
          i,
          colors.getX(i) * shade,
          colors.getY(i) * shade,
          colors.getZ(i) * shade,
        );
      }
    }
    const mesh = new THREE.InstancedMesh(
      geometry,
      vertexMaterial(),
      placements.length,
    );
    placements.forEach(([x, y, z, sx = 1, sy = sx, sz = sx], i) => {
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(sx, sy, sz);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      const radius: Record<string, number> = {
        pine: 0.7,
        candy_stake: 0.2,
        "props_v2/fort": 1.7,
        "props_v2/drift": 1.1,
        "props_v2/lantern": 0.25,
      };
      if (radius[name])
        shadows.push([x, 0, z, radius[name] * sx, radius[name] * sz]);

      if (colors)
        mesh.setColorAt(i, new THREE.Color(colors[i % colors.length]));
    });
    mesh.name = name.split("/").at(-1)!;
    yard.add(mesh);
    yard.entities.set(mesh.name, mesh);
    return mesh;
  };
  const floorGeo = new THREE.PlaneGeometry(120, 120, 1, 1);
  floorGeo.rotateX(-Math.PI / 2);
  floorGeo.setAttribute(
    "color",
    new THREE.Float32BufferAttribute(
      [1, 1, 1, 1, 1, 1, 0.81, 0.83, 1, 0.81, 0.83, 1],
      3,
    ),
  );
  const floorMaterial = vertexMaterial();
  const snowData = new Uint8Array(64 * 64 * 4);
  for (let y = 0; y < 64; y++)
    for (let x = 0; x < 64; x++) {
      const v =
        1 - 0.06 * (0.5 + 0.5 * Math.sin(x * 0.19 + Math.sin(y * 0.31) * 2));
      snowData.set([255 * v, 255 * v, 255, 255], (y * 64 + x) * 4);
    }
  const snowTexture = new THREE.DataTexture(snowData, 64, 64);
  snowTexture.colorSpace = THREE.SRGBColorSpace;
  snowTexture.generateMipmaps = true;
  snowTexture.minFilter = THREE.LinearMipmapLinearFilter;
  snowTexture.magFilter = THREE.LinearFilter;
  snowTexture.anisotropy = 4;
  snowTexture.wrapS = snowTexture.wrapT = THREE.RepeatWrapping;
  snowTexture.repeat.set(8, 8);
  snowTexture.needsUpdate = true;
  floorMaterial.map = snowTexture;
  const floor = new THREE.Mesh(floorGeo, floorMaterial);
  floor.position.set(0, -0.035, -25);
  yard.add(floor);
  // House and far hills are baked together into one fogged draw, per material.
  const house = models.get("props_v2/house")!;
  house.position.set(-2.6, 0, -12);
  house.rotation.y = Math.PI / 10;
  house.scale.setScalar(1.15);
  house.updateMatrixWorld(true);
  yard.entities.set("house", house);
  const staticParts = [coloredGeometry(house)];
  for (const [x, z, sx, sy, sz] of [
    [-12, -19, 14, 5, 6],
    [0, -22, 16, 6, 6],
    [14, -20, 13, 5, 6],
    [-17, -34, 18, 10, 6],
    [13, -38, 22, 13, 6],
  ]) {
    const hill = models.get("hill")!.clone();
    hill.position.set(x!, 0, z!);
    hill.scale.set(sx!, sy!, sz!);
    staticParts.push(coloredGeometry(hill));
  }
  const backdrop = new THREE.Mesh(
    mergeGeometries(staticParts),
    vertexMaterial(),
  );
  backdrop.name = "static_backdrop";
  yard.add(backdrop);
  place(
    "pine",
    [
      [-9, 0, -9, 1.8],
      [-6.8, 0, -10, 2.1],
      [-4.6, 0, -8.7, 1.6],
      [-1, 0, -12, 2.2],
      [2.7, 0, -9, 1.9],
      [5.2, 0, -10.3, 2.4],
      [8.2, 0, -9, 1.7],
      [11, 0, -11, 2],
      [-12, 0, -13, 2.4],
      [-3, 0, 6.3, 1.25],
      [3.35, 0, 6.6, 1.4],
    ],
    [0xb9a6f0, 0x8fd9c4, 0x7fcfb6],
  );
  place("cloud", [
    [-7, 10.5, -26, 3.4],
    [-8, 7, -30, 4.3],
    [8, 7.5, -30, 4.3],
    [6, 12, -28, 3.7],
    [-9, 16, -30, 4.3],
    [9, 18.5, -32, 4.4],
    [0, 21, -32, 4.5],
    [-13, 24, -32, 5],
    [13, 26, -32, 5],
  ]);
  place(
    "props_v2/drift",
    [
      [-2.6, 0, 6.4, 1.9],
      [2.9, 0, 6.9, 2.3],
      [0.3, 0, 7.6, 2.6],
      [-3.6, 0, 2.2, 1.2],
      [3.7, 0, 1.4, 1.4],
      [-1.4, 0, -0.8, 0.38],
      [1.4, 0, -0.8, 0.38],
    ],
    [0xd9e0ff],
  );
  const moundGeometry = coloredGeometry(models.get("mound")!);
  const moundPositions = moundGeometry.getAttribute("position");
  const moundColors = moundGeometry.getAttribute("color");
  const base = new THREE.Color("#F7F5FF"),
    shade = new THREE.Color("#C4B5EA");
  for (let i = 0; i < moundPositions.count; i++) {
    const color = moundPositions.getY(i) < 0.18 ? shade : base;
    moundColors.setXYZ(i, color.r, color.g, color.b);
  }
  const mounds = new THREE.InstancedMesh(moundGeometry, vertexMaterial(), 2);
  [
    [-1.9, 0, -4.8],
    [2.2, 0, -5.4],
  ].forEach(([x, y, z], i) => {
    dummy.position.set(x!, y!, z!);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.setScalar(1.15);
    dummy.updateMatrix();
    mounds.setMatrixAt(i, dummy.matrix);
  });
  mounds.name = "prop_mound";
  yard.add(mounds);
  yard.entities.set(mounds.name, mounds);
  const footprints = new THREE.InstancedMesh(
    new THREE.CircleGeometry(1, 12),
    new THREE.MeshBasicMaterial({
      color: 0xe9e4fa,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    }),
    8,
  );
  for (let i = 0; i < 8; i++) {
    dummy.position.set(
      -2.1 * Math.sin((Math.PI * i) / 7) +
        (i === 0 || i === 7 ? 0 : i % 2 ? 0.12 : -0.12),
      0.015,
      1.0 - i * (3 / 7),
    );
    dummy.rotation.set(-Math.PI / 2, 0, (i % 2 ? 1 : -1) * 0.12);
    dummy.scale.set(0.14, 0.24, 1);
    dummy.updateMatrix();
    footprints.setMatrixAt(i, dummy.matrix);
  }
  footprints.name = "footprint_trail";
  yard.add(footprints);
  yard.entities.set(footprints.name, footprints);
  place("props_v2/fort", [[0, 0, -2.4]]);
  place("props_v2/lantern", [
    [-2.6, 0, -1],
    [2.6, 0, -1],
  ]);
  place("props_v2/string_lights", [[0, 2.35, -9]]);
  place("candy_stake", [
    [-2.4, 0, 1],
    [2.4, 0, 1],
  ]);
  // Low-poly pickets replace the over-budget 1,260-triangle fence placeholder.
  const fenceParts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 9; i++) {
    const g = new THREE.BoxGeometry(0.16, 0.95, 0.08);
    g.translate(-2 + i * 0.5, 0.475, 0);
    fenceParts.push(g);
  }
  for (const y of [0.3, 0.65]) {
    const g = new THREE.BoxGeometry(4.4, 0.08, 0.06);
    g.translate(0, y, -0.05);
    fenceParts.push(g);
  }
  const fence = new THREE.InstancedMesh(
    mergeGeometries(fenceParts),
    new THREE.MeshBasicMaterial({ color: 0xfff6ee }),
    6,
  );
  for (let i = 0; i < 6; i++) {
    dummy.position.set((-11.25 + i * 4.5) * 0.82, 0, -8.2);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.set(0.82, 1, 1);
    dummy.updateMatrix();
    fence.setMatrixAt(i, dummy.matrix);
    shadows.push([dummy.position.x, 0, -8.2, 1.8, 0.18]);
  }
  fence.name = "fence";
  yard.entities.set("fence", fence);
  yard.add(fence);
  const shadow = new THREE.InstancedMesh(
    new THREE.CircleGeometry(1, 20),
    new THREE.MeshBasicMaterial({
      map: softTexture(),
      color: 0x7a74b8,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
    }),
    shadows.length,
  );
  shadows.forEach(([x, , z, r = 1, rz = r], i) => {
    dummy.position.set(x, 0.008, z);
    dummy.rotation.set(-Math.PI / 2, 0, 0);
    dummy.scale.set(r, rz, 1);
    dummy.updateMatrix();
    shadow.setMatrixAt(i, dummy.matrix);
  });
  yard.add(shadow);
  return yard;
}
